import React, { memo, useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setRecordDetail } from "../../store/slices/modalSlice";
import { useGetManualCallsQuery } from "../../store/api/manualCallsApi";
import { useDebounce } from "../../hooks/useDebounce";
import DispositionFilter from "../../components/Filters/DispositionFilters";
import VirtualizedTable from "../../components/Table/VirtualizedTable";

// A small, fixed column set tailored to manually uploaded recordings, kept
// separate from the shared ColumnsContext (used by the Ringba/CallGrid call
// log pages) so toggling columns there doesn't affect this page.
const MANUAL_COLUMNS = [
  { key: "callTimestamp", label: "Uploaded At", visible: true, width: 160, sortable: true },
  { key: "callerId", label: "Phone Number", visible: true, width: 140, sortable: true },
  { key: "publisherName", label: "Publisher", visible: true, width: 140, sortable: true },
  { key: "callStatus", label: "Processing", visible: true, width: 120, sortable: true },
  { key: "status", label: "Disposition", visible: true, width: 130, sortable: true },
  { key: "sub_disposition", label: "Sub Disposition", visible: true, width: 160 },
  { key: "durationSec", label: "Duration", visible: true, width: 90, sortable: true },
  { key: "summary", label: "Summary", visible: true, width: 260, truncate: true },
  { key: "reason", label: "Reason", visible: true, width: 200, truncate: true },
  { key: "originalFileName", label: "File", visible: true, width: 160, truncate: true },
];

const ManualRecordings = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [selectedDispositions, setSelectedDispositions] = useState([]);
  const [showDisposition, setShowDisposition] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("callTimestamp");
  const [sortDir, setSortDir] = useState("desc");
  const dispositionRef = useRef(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedDispositions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dispositionRef.current && !dispositionRef.current.contains(event.target)) {
        setShowDisposition(false);
      }
    };
    if (showDisposition) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDisposition]);

  const queryParams = useMemo(
    () => ({
      page,
      limit: 25,
      sortBy,
      sortDir,
      search: debouncedSearch,
      disposition: selectedDispositions.join(","),
    }),
    [page, sortBy, sortDir, debouncedSearch, selectedDispositions]
  );

  // Manual uploads process in the background (transcription -> QC analysis)
  // independent of whether this page stays open, so poll while it's mounted
  // to reflect progress (queued -> processing -> completed) automatically.
  const { data, error, isLoading, isFetching } = useGetManualCallsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 5000,
  });

  const handleRowClick = useCallback(
    (record) => {
      if (record) dispatch(setRecordDetail(record));
    },
    [dispatch]
  );

  const handleSort = useCallback((nextSortBy, nextSortDir) => {
    setSortBy(nextSortBy);
    setSortDir(nextSortDir);
  }, []);

  if (error) {
    return (
      <div className="alert alert-danger mt-2">
        Failed to load manually uploaded recordings. {error?.message || ""}
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div
        className="card"
        style={{
          backgroundColor: "var(--bg-elevated)",
          minHeight: "500px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="card-header d-flex align-items-center position-relative">
          <div className="input-group me-2" style={{ maxWidth: 320 }}>
            <input
              className="form-control form-control-sm"
              placeholder="Search phone number, publisher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ backgroundColor: "var(--bg-elevated-2)", color: "var(--text-primary)" }}
            />
            <span
              className="input-group-text"
              style={{ backgroundColor: "var(--bg-elevated-2)", borderColor: "var(--border)", color: "var(--text-secondary)" }}
            >
              <i className="bi bi-search" />
            </span>
          </div>

          <div className="ms-auto position-relative" ref={dispositionRef}>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => setShowDisposition((prev) => !prev)}
              type="button"
            >
              <i className="bi bi-funnel" /> Dispositions
              {selectedDispositions.length > 0 && (
                <span className="badge bg-warning text-dark ms-1">
                  {selectedDispositions.length}
                </span>
              )}
            </button>

            {showDisposition && (
              <div style={{ position: "absolute", top: "100%", right: 0, zIndex: 1050, marginTop: "8px" }}>
                <DispositionFilter
                  value={selectedDispositions}
                  onChange={setSelectedDispositions}
                  onClose={() => setShowDisposition(false)}
                />
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, minHeight: "300px" }}>
          <VirtualizedTable
            data={data?.data || []}
            columns={MANUAL_COLUMNS}
            loading={isLoading}
            isFetching={isFetching}
            totalCount={data?.meta?.total || 0}
            pagination={data?.meta}
            onRowClick={handleRowClick}
            onSort={handleSort}
            onPageChange={setPage}
            sortBy={sortBy}
            sortDir={sortDir}
          />
        </div>
      </div>
    </div>
  );
};

ManualRecordings.displayName = "ManualRecordings";
export default memo(ManualRecordings);
